/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 학교 검색 페이지의 학교리스트 정보 다이얼로그
 * 버전 : 1.0
 * 파일명 : SchoolListSheet.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-02, 강동형 : 저작권 추가, 더보기 버튼 추가에 따른 리다이렉트 기본값 변수 추가
 * 수정 : 2025-07-07, 강동형 : 선택한 항목 초기화 안되는 문제 해결
 * 수정 : 2025-07-09, 강동형 : total_row 값으로 렌더링 변경
 * 수정 : 2026-02-05, 강동형 : 핸들러 닫기 로직 추가
 * ================================================================ */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, Button, Menu, MenuItem, Typography,Slide, DialogTitle } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { TransitionProps } from '@mui/material/transitions';
import { BottomSheetType } from 'src/types/SchoolType';
import SchoolList from './SchoolList';
import SchoolListDetail from './SchoolListDetail';
import ArrowBackIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useDialogStore, useSchoolListDialogStore } from 'src/stores/DialogStore';
import { LoadMapDataFilter, LoadMapDataPaging } from './LoadMapData';
import AIRecommendation from '../common/aiRecommendation';
import hamburger from 'src/assets/images/Hamberger_Gray.svg'
import FilterBarSchoolSearch from './FilterBarSchoolSearch';

interface Props{
  initialSchools? : BottomSheetType[]
}

// Transition 컴포넌트 정의
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any>; },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// 필터 바 스타일링
const FilterBar = styled('div')({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', });

// 필터 버튼 스타일링
const FilterButton = styled(Button)({ display: 'flex', alignItems: 'center', marginLeft: 8, backgroundColor: 'white', color: 'black' });

const TitleContainer = styled('div')({
  display:'flex',
  justifyContent: 'center',
  alignItems:'center',
  flexDirection:'column'
})

const BottomSheetHandle = styled('div')({
  width: '68px',
  minHeight: '5px',
  borderRadius:'18px',
  backgroundColor: '#DDD',
  position: 'relative',
  top:'5px',
  margin: '0 auto',
  cursor: 'pointer'
})

const RoundButton = styled('button')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minWidth: 'auto',
  padding: '10px 20px',
  fontSize: '14px',
  borderRadius: '24px',
  backgroundColor: 'white',
  border: '1px solid #dddddd',
  boxShadow: '0px 0px 3px rgba(0, 0, 0, 0.2)',
  color: '#06799B',
  boxSizing: 'border-box',
  cursor: 'pointer',
  fontFamily: 'Pretendard',
});

const ListWrapper = styled('div')({
  maxHeight: 'calc(100vh - 160px)',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px', // 아이템 간 간격
  padding: '0px 20px'
})

const SchoolListSheet =({initialSchools}:Props) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [schools, setSchools] = useState<BottomSheetType[] | null>(initialSchools ?? null);
    const criterion = useSchoolListDialogStore((state)=>state.criterion)
    const [sortCriterion, setSortCriterion] = useState(criterion ? criterion:'이름순');
    const [selectedSchool, setSelectedSchool] = useState<BottomSheetType | null>(null);
    const isOpen = useSchoolListDialogStore((state)=>state.isOpen)
    const setField = useSchoolListDialogStore((state)=>state.setField)
    const selected = useSchoolListDialogStore((state)=>state.selected)
    const startYRef = useRef<number | null>(null);
    const loadMoreRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const [canMore,setCanMore] = useState(true)
    const [refReady, setRefReady] = useState(false);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const [isAi,setIsAi] = useState(false)
    const setFieldDialog = useDialogStore((state)=>state.setField)
    const total_rows = useDialogStore((state)=>state.total_rows)

    useEffect(()=>{
      if(selected !== null){
        setSelectedSchool(selected)
      }
      else{
        setSelectedSchool(null)
      }
    },[selected])

    // 선택한 항목 초기화
    useEffect(()=>{
      if(!isOpen){
        setField('selected',null)
      }
    },[isOpen])


    const onIntersection = async (entries : any)=>{
      const firstEntry = entries[0];
      if (firstEntry.isIntersecting && canMore) {
        const hasMore = await LoadMapDataPaging(); // 👉 await 필요함
        if (!hasMore) {
          setCanMore(false); // ✅ 더 불러올 게 없으면 중단
        }
      }
    };

    const setLoadMoreRef = useCallback((node: HTMLDivElement | null) => {
      loadMoreRef.current = node;
        // 둘 다 준비됐으면 isRefsReady true로
      if (node && scrollContainerRef.current) {
        setRefReady(true);
      }
      else {
        setRefReady(false); // ❗ ref 해제 시 false로 초기화
      }
    }, []);

    const setLoadScrollRef = useCallback((node: HTMLDivElement | null)=>{
      scrollContainerRef.current = node;
      // 둘 다 준비됐으면 isRefsReady true로
      if (node && loadMoreRef.current) {
        setRefReady(true);
      }
      else {
        setRefReady(false); // ❗ ref 해제 시 false로 초기화
      }
      // 추가된 이벤트 할당
      if(!node) return;
        const handleTouchStart = (e: TouchEvent) => {
          if (node.scrollTop === 0) {
            startYRef.current = e.touches[0].clientY;
          }
        };

        const handleTouchMove = (e: TouchEvent) => {
          if (startYRef.current !== null) {
            const deltaY = e.touches[0].clientY - startYRef.current;
            if (deltaY > 30) {
              CloseDialog();
              startYRef.current = null;
            }
          }
        };

        const handleTouchEnd = () => {
          startYRef.current = null;
        };

        node.addEventListener('touchstart', handleTouchStart);
        node.addEventListener('touchmove', handleTouchMove);
        node.addEventListener('touchend', handleTouchEnd);

        // cleanup
        return () => {
          node.removeEventListener('touchstart', handleTouchStart);
          node.removeEventListener('touchmove', handleTouchMove);
          node.removeEventListener('touchend', handleTouchEnd);
        };
    },[])
    
    useEffect(() => {
      if (selectedSchool) return;
      const target = loadMoreRef.current;
      const root = scrollContainerRef.current;
      if (!target || !root) {
        return;
      }
    
      const observer = new IntersectionObserver(onIntersection, {
        root,
        threshold: 0.1,
      });
    
      observer.observe(target);
      observerRef.current = observer;
      return () => {
        observer.disconnect();
        setRefReady(false)
      };
    }, [refReady]);

    useEffect(() => {
      if (initialSchools) {
        setSchools(initialSchools);
      } else {
        setSchools(null); // 또는 아무 것도 하지 않음
      }
    }, [initialSchools]);

    // 필터 버튼 클릭 이벤트 핸들러
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    };

    const criterionMap: Record<string, string> = {
      '이름순': 'school_name-asc',
      '최근방문순': 'visited_at-desc',
      '과거방문순': 'visited_at-asc',
      '학생 수 오름차순': 'total_student_cnt-asc',
      '학생 수 내림차순': 'total_student_cnt-desc',
    };


  const handleClose = (criterion: string = '') => {
    if (criterion) {
      const mappedCriterion = criterionMap[criterion] || '';
      const [by,order] = mappedCriterion.split('-')
      setSortCriterion(criterion);
      setFieldDialog('sort_by',by)
      setFieldDialog('sort_order',order)
    }
    setAnchorEl(null);
    LoadMapDataFilter()
  };

  const CloseDialog = ()=>{
    setField('isOpen', false);
    setIsAi(false);
    setSelectedSchool(null);
  }

  const onClickOpenDialog = ()=>{
    if(isOpen){
      setField('isOpen',false)
    }
    else{
      setField('isOpen',true)
    }
  }

  // ✅ BottomSheetHandle 드래그용
const handleHandleStart = (e: React.TouchEvent | React.MouseEvent) => {
  const y = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
  startYRef.current = y;

  document.addEventListener('touchmove', handleHandleMove as any, { passive: false });
  document.addEventListener('mousemove', handleHandleMove as any);
  document.addEventListener('touchend', handleHandleEnd);
  document.addEventListener('mouseup', handleHandleEnd);
};

const handleHandleMove = (e: TouchEvent | MouseEvent) => {
  if (startYRef.current === null) return;

  const y = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
  const deltaY = y - startYRef.current;

  if (deltaY > 30) {        // 👇 아래로 30px 이상 드래그
    CloseDialog();
    handleHandleEnd();
  }
};

const handleHandleEnd = () => {
  startYRef.current = null;

  document.removeEventListener('touchmove', handleHandleMove as any);
  document.removeEventListener('mousemove', handleHandleMove as any);
  document.removeEventListener('touchend', handleHandleEnd);
  document.removeEventListener('mouseup', handleHandleEnd);
};

  return (
    <>
      <Dialog 
        open={true}  
        onClose={CloseDialog} 
        hideBackdrop={true}
        TransitionComponent={Transition} 
        keepMounted
          PaperProps={{
              sx: {
              position: 'fixed', 
              bottom: 0, 
              margin: 0, 
              width: '100vw',
              height: isOpen ? 'calc(100vh - 153.7px)' : '112px', 
              borderTopLeftRadius: 16, 
              borderTopRightRadius: 16,
              transition: 'height 0.5s ease-in-out',
              overflow: 'visible',
              boxShadow:isOpen ? 'none':'auto',
              maxWidth: '100vw',
              }
          }}
        sx={{
            pointerEvents: 'none', // ❌ Dialog 전체는 클릭 못하게 막음
            '& .MuiPaper-root': {
            pointerEvents: 'auto', // ✅ 다시 시트만 클릭 가능하게 풀어줌
        }}}
        >
          {!isOpen &&
          <div style={{position:'relative'}}>
            <RoundButton onClick={()=>onClickOpenDialog()} style={{gap:'6px', color:'#000014', padding:'9px 13px', top:'-50px', position:'absolute',left: '50%', transform: 'translateX(-50%)'}}>
              <img src={hamburger}/> 
              <span> 목록 보기</span>
            </RoundButton>
          </div>}
          <BottomSheetHandle 
            onClick={CloseDialog}
            onTouchStart={handleHandleStart}
            onMouseDown={handleHandleStart}
          />
          <TitleContainer>
            {selectedSchool ? 
            isAi ? 
            <>
            <AIRecommendation selectedSchool={selectedSchool} onBackClick={setIsAi}/>
            </>
            :(
              <>
              <DialogTitle sx={{fontSize:16, fontWeight:700, padding:'11px'}}>학교 상세 정보</DialogTitle>
              <ArrowBackIcon sx={{
                  fontSize:'24px',
                  position: 'absolute',
                  display:'flex',
                  left: 20
                  }}
                  onClick={()=>setSelectedSchool(null)}/>
              </>) : 
            (
              <div style={{display:'flex',justifyContent:'flex-start',flexDirection:'column', width:'calc(100vw - 40px)'}}>
                <FilterBarSchoolSearch/>
                <FilterBar sx={{zIndex: 1, display:'flex', flexDirection:'row', justifyContent:'space-between', alignItems:'center', width:'100%'}}>
                  <Typography variant="body2" sx ={{color:'#7F7F89', fontSize:'12px', fontWeight:500}}>
                    총 <span style={{color:'black',fontSize:'16px'}}>{total_rows} </span>건
                    </Typography>
                  <div>
                  <FilterButton onClick={handleClick} endIcon={<ArrowDropDownIcon />}>
                    {sortCriterion}
                  </FilterButton>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleClose()} sx={{fontWeight: 400}}>
                    <MenuItem onClick={() => handleClose('이름순')}>이름순</MenuItem>
                    <MenuItem onClick={() => handleClose('최근방문순')}>최근방문순</MenuItem>
                    <MenuItem onClick={() => handleClose('과거방문순')}>과거방문순</MenuItem>
                    <MenuItem onClick={() => handleClose('학생 수 오름차순')}>학생 수 오름차순</MenuItem>
                    <MenuItem onClick={() => handleClose('학생 수 내림차순')}>학생 수 내림차순</MenuItem>
                  </Menu>
                  </div>
                </FilterBar>
              </div>
                  )}
          </TitleContainer>
          {selectedSchool ? 
            !isAi ? 
              (<SchoolListDetail school={selectedSchool} setAiState={setIsAi}/>)
            :
              (<></>)
              :
              (<ListWrapper ref={setLoadScrollRef}>
                {schools?.map((schools, index)=>(
                  <SchoolList key={index} school={schools} setState={setSelectedSchool}/>
                ))}
                <div ref={setLoadMoreRef} style={{ minHeight: '1px' }}></div>
              </ListWrapper>)
            }
          </Dialog>
        </>
  )
}

export default SchoolListSheet